import { colors, white } from '@kmns-internal/furikake'
import { Delete, DeleteOutlined, MoreHoriz } from '@mui/icons-material'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Employee, PlaceNodeTypeEnum } from '@ulysses-inc/harami_api_client'
import React, { MouseEvent, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getUniqueFlattenNodes } from 'src/exShared/util/place/getUniqueFlattenNodes'
import { flattenNodes } from 'src/exShared/util/place/placeNode'
import employeesOperations from 'src/state/ducks/employees/operations'
import { RootState } from 'src/state/store'

interface Props {
  chooseEmployees: { [key: string]: boolean }
  setChooseEmployees: (employees: { [key: string]: boolean }) => void
}

const EmployeeList: React.FC<Props> = (props: Props) => {
  const dispatch = useDispatch()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }
  const handleMenuClose = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(null)
  }
  const { chooseEmployees, setChooseEmployees } = props

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(
    null,
  )

  const { employees = [] } = useSelector(
    (state: RootState) => state.employeesState.employees,
  )

  const {
    places: { places },
    placeGroups: { nodes },
  } = useSelector((state: RootState) => state.placesState)

  const placeNodes = getUniqueFlattenNodes([
    ...flattenNodes(nodes ?? []),
    ...(places ?? []),
  ])

  useEffect(() => {
    Object.keys(chooseEmployees).forEach(employeeUUID => {
      const isContain =
        employees.findIndex(employee => employee.uuid === employeeUUID) >= 0
      if (!isContain) {
        // eslint-disable-next-line react-hooks/immutability -- 関数の宣言順序による既存パターンのため、リファクタリング時に修正する
        handleCheckEmployee(employeeUUID, false)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees])

  const openEditEmployee = (employee: Employee) => {
    employeesOperations.updateActiveEmployeeUUID(dispatch, employee.uuid ?? '')
    employeesOperations.changeIsShowEditEmployeeDrawer(dispatch, true)
  }
  const deleteEmployee = (uuid: string) => {
    employeesOperations.deleteEmployee(dispatch, uuid)
  }

  const isCheckedAll = () => {
    if (employees.length === 0) return false
    const unChecked = employees.filter(
      (employee: Employee) =>
        employee.uuid !== undefined &&
        employee.uuid !== '' &&
        !Object.hasOwnProperty.call(chooseEmployees, employee.uuid),
    )
    return unChecked.length === 0
  }

  const handleCheckAll = (checked: boolean) => {
    if (checked) {
      employees.forEach((employee: Employee) => {
        if (employee.uuid === undefined || employee.uuid === '') {
          return
        }
        chooseEmployees[employee.uuid] = true
      })
      setChooseEmployees({ ...chooseEmployees })
      return
    }
    employees.forEach((employee: Employee) => {
      if (employee.uuid === undefined || employee.uuid === '') {
        return
      }
      delete chooseEmployees[employee.uuid]
    })
    setChooseEmployees({ ...chooseEmployees })
  }

  const handleCheckEmployee = (employeeUUID: string, checked: boolean) => {
    if (checked) {
      // eslint-disable-next-line react-hooks/immutability -- 関数の宣言順序による既存パターンのため、リファクタリング時に修正する
      chooseEmployees[employeeUUID] = true
      setChooseEmployees({ ...chooseEmployees })
    } else {
      delete chooseEmployees[employeeUUID]
      setChooseEmployees({ ...chooseEmployees })
    }
  }
  return (
    <>
      <Table sx={{ bgcolor: white, width: '100%' }} stickyHeader>
        <TableHead sx={{ backgroundColor: white }}>
          <TableRow>
            <TableCell
              size="small"
              sx={{ backgroundColor: white, width: '3%' }}
            >
              <Checkbox
                checked={isCheckedAll()}
                onChange={e => handleCheckAll(e.target.checked)}
              />
            </TableCell>
            <TableCell
              size="small"
              sx={{ backgroundColor: white, width: '30%' }}
            >
              従業員管理番号
            </TableCell>
            <TableCell
              size="small"
              sx={{ backgroundColor: white, width: '30%' }}
            >
              従業員名
            </TableCell>
            <TableCell
              size="small"
              sx={{ backgroundColor: white, width: '30%' }}
            >
              現場名
            </TableCell>
            <TableCell
              size="small"
              sx={{
                whiteSpace: 'normal',
                backgroundColor: white,
              }}
              aria-label="操作"
            ></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {employees.map(employee => (
            <TableRow
              key={employee.uuid}
              onClick={() => openEditEmployee(employee)}
              sx={{
                cursor: 'pointer',
                '&:hover': { backgroundColor: colors.action.hover },
                position: 'relative',
              }}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  checked={
                    employee.uuid !== undefined && employee.uuid !== ''
                      ? Object.hasOwnProperty.call(
                          chooseEmployees,
                          employee.uuid,
                        )
                      : false
                  }
                  onClick={e => {
                    e.stopPropagation()
                  }}
                  onChange={e => {
                    e.stopPropagation()
                    if (employee.uuid) {
                      handleCheckEmployee(employee.uuid, e.target.checked)
                    }
                  }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {employee.employeeCode}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {employee.name}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {employee.placeNodes && employee.placeNodes.length > 0
                    ? employee.placeNodes
                        .map(node => {
                          if (node.type === PlaceNodeTypeEnum.Place) {
                            return (
                              placeNodes.find(
                                placeNode => placeNode.uuid === node.uuid,
                              )?.place?.name || ''
                            )
                          } else {
                            return (
                              placeNodes.find(
                                placeNode => placeNode.uuid === node.uuid,
                              )?.placeGroup?.name || ''
                            )
                          }
                        })
                        .join(' ')
                    : ''}
                </Typography>
              </TableCell>
              <TableCell
                size="small"
                sx={{ minWidth: '96px' }}
                align="right"
                aria-label="操作"
              >
                <IconButton
                  onClick={e => {
                    handleMenuClick(e)
                    setEmployeeToDelete(employee)
                  }}
                  aria-label="操作メニューを開く"
                >
                  <MoreHoriz />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {/* メニュー */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={event => {
            setDeleteDialogOpen(true)
            handleMenuClose(event)
          }}
          sx={{
            color: colors.error.main,
            '&:hover': {
              backgroundColor: colors.error.hover,
              color: colors.error.main,
            },
            gap: 1,
          }}
        >
          <DeleteOutlined />
          削除
        </MenuItem>
      </Menu>
      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          「{employeeToDelete?.name}」の削除を実行しますがよろしいですか?
        </DialogTitle>
        <DialogActions>
          <Button
            color="primary"
            variant="outlined"
            onClick={() => setDeleteDialogOpen(false)}
          >
            キャンセル
          </Button>
          <Button
            onClick={() => {
              deleteEmployee(employeeToDelete?.uuid ?? '')
              setDeleteDialogOpen(false)
            }}
            color="error"
            variant="contained"
            startIcon={<Delete />}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default EmployeeList
